import { type RootState, store } from "@store/index";

import { type AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { type LayoutProps } from "skedyul-ui/ui";
import { close as closeDialog } from "@store/features/layout/dialogSlice";
import {
  generateDialogFromSchema,
  getDialogParams,
} from "../../schemas/dialog.schema";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useTheme } from "@teispace/next-themes";

type ThemeContext = ReturnType<typeof useTheme>;
import { generateSideNavigationComponent } from "@schemas/sideNavigation.schema";

export type AppLayoutState = {
  workplace: RootState["workplace"];
  sideNavigation: RootState["sideNavigation"];
  dialog: RootState["dialog"];
  workflows: RootState["workflows"];
  agents: RootState["agents"];
};

dayjs.extend(relativeTime);

type SideNavigationContent = ReturnType<typeof generateSideNavigationComponent>;

const emptySideNavigation = (
  position: "left" | "right",
  width: string,
): SideNavigationContent => ({
  side: position,
  isOpen: false,
  width,
  component: {
    component: "Custom",
    props: { children: null },
  },
});

const safeGenerateSideNavigation = (
  args: Parameters<typeof generateSideNavigationComponent>[0],
): SideNavigationContent => {
  try {
    return generateSideNavigationComponent(args);
  } catch (error) {
    console.error("Failed to generate side navigation:", error);
    return emptySideNavigation(args.position, args.width);
  }
};

// New helper function to generate dialog content
export const getDialogContent = async (
  layoutState: AppLayoutState,
  fullState: RootState,
  dialog: AppLayoutState["dialog"],
  t: unknown,
  router: AppRouterInstance,
) => {
  try {
    return await generateDialogFromSchema(
      dialog.schema,
      getDialogParams(fullState, dialog.params || {}),
      router,
    );
  } catch (error) {
    console.error("Failed to generate dialog content:", error);
    return {
      sections: [],
      title: "Error",
      description: "Failed to load dialog content",
      actions: [],
    };
  }
};

export const transformAppStateToLayoutContent = (
  appState: AppLayoutState,
  fullState: RootState,
  viewPortIsMobile: boolean,
  themeProvider: ThemeContext,
  _t: unknown,
  {
    pathname,
    params,
    router,
  }: {
    params: { subdomain: string };
    pathname: string | null;
    router: AppRouterInstance;
  },
): LayoutProps => {
  const {
    dialog,
    sideNavigation: { left, right },
  } = appState;

  return {
    children: [],
    dialog: {
      open: dialog.open,
      handleClose: () => {
        store.dispatch(closeDialog());
      },
      sections: [],
      title: "",
      description: "",
      actions: [],
    },
    sideNavigation: {
      left: safeGenerateSideNavigation({
        position: "left",
        width: left.width,
        viewPortIsMobile,
        themeContext: themeProvider,
        routerContext: {
          router,
          params,
          pathname: pathname || "",
        },
        state: fullState,
        props: left.params,
      }),
      right: safeGenerateSideNavigation({
        position: "right",
        width: right.width,
        viewPortIsMobile,
        themeContext: themeProvider,
        routerContext: {
          router,
          params,
          pathname: pathname || "",
        },
        state: fullState,
        props: right.params,
      }),
    },
  };
};
